import i18n from '@/i18n'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { CompanySettings, LeaveType } from '@/types/settings.types'
import { settingsService } from '@/services/settings.service'

export const useSettingsStore = defineStore('settings', () => {
  const companySettings = ref<CompanySettings | null>(null)
  const leaveTypes = ref<LeaveType[]>([])
  const isLoading = ref(false)
  const error = ref<string>('')

  async function fetchCompanySettings() {
    isLoading.value = true
    error.value = ''
    try {
      const response = await settingsService.getCompanySettings()
      if (response.success && response.data) {
        companySettings.value = response.data
      } else {
        error.value = response.message || i18n.global.t('workflow.settingsLoadFailed')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || i18n.global.t('workflow.settingsLoadError')
    } finally {
      isLoading.value = false
    }
  }

  async function fetchLeaveTypes() {
    isLoading.value = true
    error.value = ''
    try {
      const response = await settingsService.getLeaveTypes()
      if (response.success && response.data) {
        leaveTypes.value = response.data
      } else {
        error.value = response.message || i18n.global.t('workflow.leaveTypesLoadFailed')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || i18n.global.t('workflow.leaveTypesLoadError')
    } finally {
      isLoading.value = false
    }
  }

  async function updateCompanySettings(updates: Partial<CompanySettings>) {
    isLoading.value = true
    error.value = ''
    try {
      const response = await settingsService.updateCompanySettings(updates)
      if (response.success && response.data) {
        companySettings.value = response.data
      } else {
        error.value = response.message || i18n.global.t('workflow.settingsUpdateFailed')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || i18n.global.t('workflow.settingsUpdateError')
    } finally {
      isLoading.value = false
    }
  }

  return {
    companySettings,
    leaveTypes,
    isLoading,
    error,
    fetchCompanySettings,
    fetchLeaveTypes,
    updateCompanySettings
  }
})
